import React, { useState, useEffect } from 'react';
import { useLazyCommand, useEvent } from "@soap/modules";
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { secondsToMMSS, mmssToSeconds } from '../../utils/timeFormat';
import { timingModeKeys } from './types';
import { X } from 'lucide-react';

const viewStates = {
  ready: 0,
  removing: 1,
  updatingTime: 2,
  complete: 3
};

export function RemoveQuestionDialog({ 
  assessmentId, 
  questionId, 
  questionTitle,
  timingMode,
  sectionTimeLimit,
  sectionQuestionCount,
  sectionId,
  overallTimeLimit,
  totalQuestionCount,
  onClose,
  onSectionTimeUpdate,
  onOverallTimeUpdate,
  onRefresh
}: { 
  assessmentId: string; 
  questionId: string; 
  questionTitle: string;
  timingMode: string;
  sectionTimeLimit?: number;
  sectionQuestionCount?: number;
  sectionId?: string;
  overallTimeLimit?: number;
  totalQuestionCount?: number;
  onClose: () => void;
  onSectionTimeUpdate?: (newTime: number) => void;
  onOverallTimeUpdate?: (newTime: number) => void;
  onRefresh: () => void;
}) {
  const [viewState, setViewState] = useState(viewStates.ready);
  const [removeQuestionTrigger] = useLazyCommand("C194v1_RemoveQuestionFromAssessment");
  
  const isPerSection = timingMode === timingModeKeys.PerSection;
  const isPerAssessment = timingMode === timingModeKeys.PerAssessment;
  const shouldShowTimeAdjustment = (isPerSection || isPerAssessment) && 
    ((isPerSection && sectionTimeLimit && sectionQuestionCount && sectionQuestionCount > 0) ||
     (isPerAssessment && overallTimeLimit && totalQuestionCount && totalQuestionCount > 0));
  
  const [adjustTime, setAdjustTime] = useState(shouldShowTimeAdjustment);
  const [newTimeDisplay, setNewTimeDisplay] = useState('');
  const [pendingTimeUpdate, setPendingTimeUpdate] = useState<{ newTime: number; isPerSection: boolean; sectionId?: string } | null>(null);

  useEffect(() => {
    if (shouldShowTimeAdjustment) {
      if (isPerSection && sectionTimeLimit && sectionQuestionCount) {
        // If this is the last question, new time should be 0
        if (sectionQuestionCount === 1) {
          setNewTimeDisplay(secondsToMMSS(0));
        } else {
          // Calculate: current time - (current time / number of questions)
          const timePerQuestion = sectionTimeLimit / sectionQuestionCount;
          const newTime = sectionTimeLimit - timePerQuestion;
          setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
        }
      } else if (isPerAssessment && overallTimeLimit && totalQuestionCount) {
        // If this is the last question, new time should be 0
        if (totalQuestionCount === 1) {
          setNewTimeDisplay(secondsToMMSS(0));
        } else {
          // Calculate: current time - (current time / total number of questions)
          const timePerQuestion = overallTimeLimit / totalQuestionCount;
          const newTime = overallTimeLimit - timePerQuestion;
          setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
        }
      }
    }
  }, [adjustTime, shouldShowTimeAdjustment, isPerSection, isPerAssessment, sectionTimeLimit, sectionQuestionCount, overallTimeLimit, totalQuestionCount]);

  // Listen for question removal event, then update time if needed
  useEvent({
    eventName: "E160v1_QuestionRemovedFromAssessment",
    onEventReceived: (event: any) => {
      const eventQuestionId = event.e160_QuestionId || event.E160_QuestionId;
      console.log('[RemoveQuestionDialog] E160v1_QuestionRemovedFromAssessment received', {
        eventQuestionId,
        currentQuestionId: questionId,
        viewState,
        viewStates,
        hasPendingTimeUpdate: !!pendingTimeUpdate,
        pendingTimeUpdate
      });
      
      if (viewState === viewStates.removing && eventQuestionId === questionId) {
        // Question was removed, now update time if needed
        if (pendingTimeUpdate) {
          console.log('[RemoveQuestionDialog] Sending time update command');
          setViewState(viewStates.updatingTime);
          if (pendingTimeUpdate.isPerSection && pendingTimeUpdate.sectionId && onSectionTimeUpdate) {
            onSectionTimeUpdate(pendingTimeUpdate.newTime);
          } else if (!pendingTimeUpdate.isPerSection && onOverallTimeUpdate) {
            onOverallTimeUpdate(pendingTimeUpdate.newTime);
          }
          // Don't clear pendingTimeUpdate yet - we'll wait for the time update event
        } else {
          console.log('[RemoveQuestionDialog] No time update needed, closing');
          // No time update needed, just close - the event listener will update the UI
          setViewState(viewStates.complete);
          onClose();
        }
      }
    }
  }, [viewState, questionId, pendingTimeUpdate, onSectionTimeUpdate, onOverallTimeUpdate, onClose]);

  // Listen for timing update event (fired after time limit update)
  useEvent({
    eventName: "E268v1_AssessmentTimingUpdated",
    onEventReceived: (event: any) => {
      const eventAssessmentId = event.e268_AssessmentId || event.E268_AssessmentId;
      
      // Filter by assessment ID and check if we're waiting for a time update
      if (eventAssessmentId === assessmentId && 
          viewState === viewStates.updatingTime && 
          pendingTimeUpdate) {
        
        // Time update is complete! (Question already removed by event listener, timing updated by E268 event handler)
        setPendingTimeUpdate(null);
        setViewState(viewStates.complete);
        onClose();
      }
    }
  }, [assessmentId, viewState, pendingTimeUpdate, onClose]);

  const handleRemove = () => {
    console.log('[RemoveQuestionDialog] handleRemove called', {
      adjustTime,
      shouldShowTimeAdjustment,
      newTimeDisplay
    });
    
    // Store the time update info if needed
    if (adjustTime && shouldShowTimeAdjustment) {
      const newTimeInSeconds = mmssToSeconds(newTimeDisplay);
      console.log('[RemoveQuestionDialog] Setting pendingTimeUpdate', {
        newTimeInSeconds,
        isPerSection,
        sectionId
      });
      if (newTimeInSeconds !== null && newTimeInSeconds >= 0 && newTimeInSeconds <= 3600) {
        setPendingTimeUpdate({
          newTime: newTimeInSeconds,
          isPerSection: isPerSection,
          sectionId: sectionId
        });
      }
    }

    // Send remove command first
    console.log('[RemoveQuestionDialog] Sending remove command');
    setViewState(viewStates.removing);
    removeQuestionTrigger({
      C194_AssessmentId: assessmentId,
      C194_QuestionId: questionId,
    });
  };

  const getCalculationText = () => {
    if (isPerSection && sectionTimeLimit && sectionQuestionCount) {
      if (sectionQuestionCount === 1) {
        return `Current section time: ${secondsToMMSS(sectionTimeLimit)}\nRemoving last question\nNew section time: ${secondsToMMSS(0)}`;
      } else {
        const timePerQuestion = sectionTimeLimit / sectionQuestionCount;
        const newTime = sectionTimeLimit - timePerQuestion;
        return `Current section time: ${secondsToMMSS(sectionTimeLimit)}\nTime per question: ${secondsToMMSS(Math.floor(timePerQuestion))} (${secondsToMMSS(sectionTimeLimit)} ÷ ${sectionQuestionCount})\nNew section time: ${secondsToMMSS(Math.max(0, Math.floor(newTime)))} (${secondsToMMSS(sectionTimeLimit)} - ${secondsToMMSS(Math.floor(timePerQuestion))})`;
      }
    } else if (isPerAssessment && overallTimeLimit && totalQuestionCount) {
      if (totalQuestionCount === 1) {
        return `Current overall time: ${secondsToMMSS(overallTimeLimit)}\nRemoving last question\nNew overall time: ${secondsToMMSS(0)}`;
      } else {
        const timePerQuestion = overallTimeLimit / totalQuestionCount;
        const newTime = overallTimeLimit - timePerQuestion;
        return `Current overall time: ${secondsToMMSS(overallTimeLimit)}\nTime per question: ${secondsToMMSS(Math.floor(timePerQuestion))} (${secondsToMMSS(overallTimeLimit)} ÷ ${totalQuestionCount})\nNew overall time: ${secondsToMMSS(Math.max(0, Math.floor(newTime)))} (${secondsToMMSS(overallTimeLimit)} - ${secondsToMMSS(Math.floor(timePerQuestion))})`;
      }
    }
    return '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md p-6 m-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-semibold mb-4 pr-8">Remove Question</h3>
        <p className="mb-2">Please confirm you want to remove the question "{questionTitle}"?</p>
        <p className="text-sm text-gray-600 mb-4">This will not remove the question from any previously shared assessments.</p>
        
        {shouldShowTimeAdjustment && (
          <div className="mb-4 p-4 bg-action-blue/10 border border-action-blue/30 rounded-lg">
            <label className="flex items-center space-x-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={adjustTime}
                onChange={(e) => {
                  setAdjustTime(e.target.checked);
                  // Reset to calculated value when unchecking
                  if (!e.target.checked) {
                    if (isPerSection && sectionTimeLimit && sectionQuestionCount) {
                      if (sectionQuestionCount === 1) {
                        setNewTimeDisplay(secondsToMMSS(0));
                      } else {
                        const timePerQuestion = sectionTimeLimit / sectionQuestionCount;
                        const newTime = sectionTimeLimit - timePerQuestion;
                        setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
                      }
                    } else if (isPerAssessment && overallTimeLimit && totalQuestionCount) {
                      if (totalQuestionCount === 1) {
                        setNewTimeDisplay(secondsToMMSS(0));
                      } else {
                        const timePerQuestion = overallTimeLimit / totalQuestionCount;
                        const newTime = overallTimeLimit - timePerQuestion;
                        setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
                      }
                    }
                  }
                }}
                className="w-4 h-4 text-action-blue"
              />
              <span className="text-sm font-medium">Reduce the time limit accordingly?</span>
            </label>
            
            <div className="ml-6 space-y-3">
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-2">Calculation:</p>
                <div className="text-xs font-mono whitespace-pre-line bg-white p-2 rounded border border-gray-200">
                  {getCalculationText()}
                </div>
              </div>
              <div>
                <Label htmlFor="new-time" className="text-sm font-medium">
                  New Time Limit (MM:SS):
                </Label>
                <Input
                  id="new-time"
                  type="text"
                  placeholder="MM:SS"
                  value={newTimeDisplay}
                  onChange={(e) => setNewTimeDisplay(e.target.value)}
                    onBlur={() => {
                      const seconds = mmssToSeconds(newTimeDisplay);
                      if (seconds === null || seconds < 0 || seconds > 3600) {
                        // Reset to calculated value
                        if (isPerSection && sectionTimeLimit && sectionQuestionCount) {
                          if (sectionQuestionCount === 1) {
                            setNewTimeDisplay(secondsToMMSS(0));
                          } else {
                            const timePerQuestion = sectionTimeLimit / sectionQuestionCount;
                            const newTime = sectionTimeLimit - timePerQuestion;
                            setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
                          }
                        } else if (isPerAssessment && overallTimeLimit && totalQuestionCount) {
                          if (totalQuestionCount === 1) {
                            setNewTimeDisplay(secondsToMMSS(0));
                          } else {
                            const timePerQuestion = overallTimeLimit / totalQuestionCount;
                            const newTime = overallTimeLimit - timePerQuestion;
                            setNewTimeDisplay(secondsToMMSS(Math.max(0, Math.floor(newTime))));
                          }
                        }
                      } else {
                        setNewTimeDisplay(secondsToMMSS(seconds));
                      }
                    }}
                  className="w-24 mt-1"
                  pattern="[0-9]{1,2}:[0-5][0-9]"
                  disabled={!adjustTime}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={viewState === viewStates.removing || viewState === viewStates.updatingTime}
          >
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleRemove}
            disabled={viewState === viewStates.removing || viewState === viewStates.updatingTime}
          >
            {viewState === viewStates.removing ? 'Removing...' : 
             viewState === viewStates.updatingTime ? 'Updating...' : 
             'Remove'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

